import React, { useState, useCallback } from 'react';
import {
  Handle,
  Position,
  getSmoothStepPath,
  EdgeProps,
  NodeTypes,
  NodeProps,
} from 'reactflow';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipPortal } from '@/components/ui/tooltip';
import {
  Play,
  Square,
  Zap,
  GitBranch,
  Clock,
  Mail,
  MessageSquare,
  Code,
  Database,
  Globe,
  FileJson,
  CheckCircle,
  Plug,
  Sun,
  Power,
  Droplets,
  Wifi,
  Shield,
} from 'lucide-react';
import { CornerDownRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { RectangleAnnotation, LabelAnnotation } from '@/components/workflow/AnnotationNodes';
import { WorkflowNodeData } from '@/types/workflow-editor';

// Node type definitions based on backend
export const nodeTypeConfig = {
  start: { label: 'Start', icon: Play, color: 'bg-green-500', description: 'Workflow entry point' },
  end: { label: 'End', icon: Square, color: 'bg-red-500', description: 'Workflow end point' },
  goto_start: { label: 'Goto Start', icon: CornerDownRight, color: 'bg-purple-500', description: 'Jump to a Start node' },
  condition: { label: 'Condition', icon: GitBranch, color: 'bg-yellow-500', description: 'Conditional branching' },
  action: { label: 'Action', icon: Zap, color: 'bg-blue-500', description: 'Execute an action' },
  delay: { label: 'Delay', icon: Clock, color: 'bg-purple-500', description: 'Wait/delay execution' },
  email: { label: 'Email', icon: Mail, color: 'bg-indigo-500', description: 'Send email notification' },
  sms: { label: 'SMS', icon: MessageSquare, color: 'bg-pink-500', description: 'Send SMS notification' },
  webhook: { label: 'Webhook', icon: Globe, color: 'bg-cyan-500', description: 'HTTP webhook call' },
  transform: { label: 'Transform', icon: Code, color: 'bg-orange-500', description: 'Transform data' },
  database: { label: 'Database', icon: Database, color: 'bg-emerald-500', description: 'Database operation' },
  api: { label: 'API Call', icon: FileJson, color: 'bg-teal-500', description: 'External API call' },
  loop: { label: 'Loop', icon: GitBranch, color: 'bg-violet-500', description: 'Iterate over items' },
  parallel: { label: 'Parallel', icon: GitBranch, color: 'bg-amber-500', description: 'Execute in parallel' },
  switch: { label: 'Switch', icon: GitBranch, color: 'bg-rose-500', description: 'Multi-way branch' },
  variable_assignment: { label: 'Set Value', icon: Code, color: 'bg-slate-500', description: 'Set a variable or element property' },
  variable_set: { label: 'Set Value', icon: Code, color: 'bg-slate-500', description: 'Set a variable or element property' },
  button_check: { label: 'Button Check', icon: CheckCircle, color: 'bg-lime-500', description: 'Check if button was pressed' },
  noop: { label: 'No-Op', icon: GitBranch, color: 'bg-gray-400', description: 'Pass-through / loop point' },
  // Kasa/Tapo smart device nodes
  kasa_device_control: { label: 'Kasa Control', icon: Power, color: 'bg-cyan-600', description: 'Control Kasa/Tapo smart device' },
  kasa_get_status: { label: 'Kasa Status', icon: Plug, color: 'bg-cyan-500', description: 'Get Kasa/Tapo device status' },
  kasa_trigger: { label: 'Kasa Trigger', icon: Sun, color: 'bg-cyan-400', description: 'Trigger on Kasa device change' },
  // Hydrawise irrigation nodes
  hydrawise_get_status: { label: 'Hydrawise Status', icon: Droplets, color: 'bg-blue-600', description: 'Get Hydrawise zone status' },
  hydrawise_zone_control: { label: 'Hydrawise Control', icon: Droplets, color: 'bg-blue-500', description: 'Control Hydrawise zone' },
  hydrawise_trigger: { label: 'Hydrawise Trigger', icon: Droplets, color: 'bg-blue-400', description: 'Trigger on Hydrawise event' },
  // UniFi Network nodes
  unifi_network_get_devices: { label: 'UniFi Devices', icon: Wifi, color: 'bg-indigo-600', description: 'Get UniFi network devices' },
  unifi_network_get_clients: { label: 'UniFi Clients', icon: Wifi, color: 'bg-indigo-500', description: 'Get UniFi network clients' },
  unifi_network_device_action: { label: 'UniFi Action', icon: Wifi, color: 'bg-indigo-400', description: 'UniFi device action' },
  unifi_network_trigger: { label: 'UniFi Trigger', icon: Wifi, color: 'bg-indigo-300', description: 'Trigger on UniFi event' },
  // UniFi Protect nodes
  unifi_protect_get_status: { label: 'Protect Status', icon: Shield, color: 'bg-purple-600', description: 'Get UniFi Protect status' },
  unifi_protect_action: { label: 'Protect Action', icon: Shield, color: 'bg-purple-500', description: 'UniFi Protect action' },
  unifi_protect_trigger: { label: 'Protect Trigger', icon: Shield, color: 'bg-purple-400', description: 'Trigger on Protect event' },
};

// Helper function to convert cron expression to human-readable text
const cronToHumanReadable = (cron: string): string => {
  const parts = cron.split(' ');
  if (parts.length !== 5) return cron;

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

  // Helper to format time
  const formatTime = (h: string, m: string): string => {
    const hourNum = parseInt(h);
    const minuteNum = parseInt(m);
    const period = hourNum < 12 ? 'AM' : 'PM';
    const hour12 = hourNum === 0 ? 12 : hourNum > 12 ? hourNum - 12 : hourNum;
    return `${hour12}:${minuteNum.toString().padStart(2, '0')} ${period}`;
  };

  // Build the description
  let description = '';

  // Handle frequency
  if (minute === '*' && hour === '*') {
    description = 'Every minute';
  } else if (hour === '*') {
    if (minute.startsWith('*/')) {
      const interval = minute.substring(2);
      description = `Every ${interval} minutes`;
    } else {
      description = `Every hour at :${minute.padStart(2, '0')}`;
    }
  } else if (hour.startsWith('*/')) {
    const interval = hour.substring(2);
    if (minute === '0') {
      description = `Every ${interval} hours`;
    } else {
      description = `Every ${interval} hours at :${minute.padStart(2, '0')}`;
    }
  } else {
    // Specific time
    description = `At ${formatTime(hour, minute)}`;
  }

  // Add day of week filter
  if (dayOfWeek !== '*') {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dayName = days[parseInt(dayOfWeek)] || `day ${dayOfWeek}`;
    description += ` on ${dayName}`;
  }

  // Add day of month filter (only if day of week is not set)
  if (dayOfMonth !== '*' && dayOfWeek === '*') {
    const daySuffix = (day: number) => {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };
    const dayNum = parseInt(dayOfMonth);
    description += ` on the ${dayNum}${daySuffix(dayNum)}`;
  }

  // Add month filter
  if (month !== '*') {
    const months = ['', 'January', 'February', 'March', 'April', 'May', 'June',
                    'July', 'August', 'September', 'October', 'November', 'December'];
    const monthName = months[parseInt(month)] || `month ${month}`;
    description += ` in ${monthName}`;
  }

  // Add "of every month" if we have a day but no month specified
  if (dayOfMonth !== '*' && month === '*' && dayOfWeek === '*') {
    description += ' of every month';
  }

  return description;
};

// Helper function to generate tooltip content for nodes
export const getNodeTooltipContent = (data: WorkflowNodeData): React.ReactNode => {
  if (!data.config) return null;

  switch (data.type) {
    case 'start': {
      const triggerType = data.config.triggerType || 'manual';
      if (triggerType === 'manual') {
        return (
          <div className="space-y-1">
            <div className="font-semibold">Trigger Configuration</div>
            <div>Type: Manual</div>
          </div>
        );
      } else if (triggerType === 'scheduled') {
        const cron = data.config.cron || '0 9 * * *';
        const timezone = data.config.timezone || 'America/Los_Angeles';
        const enabled = data.config.enabled !== false;
        const humanReadable = cronToHumanReadable(cron);
        return (
          <div className="space-y-1">
            <div className="font-semibold">Trigger Configuration</div>
            <div>Type: Scheduled</div>
            <div className="text-sm font-medium text-blue-700">{humanReadable}</div>
            <div className="text-xs text-gray-600">Timezone: {timezone}</div>
            <div>Status: {enabled ? 'Enabled' : 'Disabled'}</div>
          </div>
        );
      } else if (triggerType === 'mqtt') {
        const topic = data.config.mqttTopic || '(not set)';
        const enabled = data.config.enabled !== false;
        const variableMappings = data.config.variableMappings || [];
        return (
          <div className="space-y-1">
            <div className="font-semibold">Trigger Configuration</div>
            <div>Type: MQTT</div>
            <div className="text-sm font-medium text-green-700">Topic: {topic}</div>
            <div>Status: {enabled ? 'Enabled' : 'Disabled'}</div>
            {variableMappings.length > 0 && (
              <div className="mt-2">
                <div className="text-xs font-semibold text-gray-700">Variables:</div>
                <div className="text-xs space-y-0.5 mt-1">
                  {variableMappings.map((mapping, idx: number) => (
                    <div key={idx} className="font-mono">
                      {mapping.jsonPath} → {mapping.targetVariable}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      } else if (triggerType === 'webhook') {
        return (
          <div className="space-y-1">
            <div className="font-semibold">Trigger Configuration</div>
            <div>Type: Webhook</div>
          </div>
        );
      }
      return null;
    }

    case 'variable_set':
    case 'variable_assignment': {
      const targetType = data.config.targetType || 'variable';

      // Element property mode
      if (targetType === 'element') {
        const elementId = data.config.elementId;
        const propertyName = data.config.propertyName;
        const value = data.config.value;

        if (!elementId || !propertyName) {
          return (
            <div className="text-yellow-600">
              Element property not configured
            </div>
          );
        }

        return (
          <div className="space-y-1">
            <div className="font-semibold">Set Element Property</div>
            <div>Element: <span className="font-mono">{elementId}</span></div>
            <div>Property: <span className="font-mono">{propertyName}</span></div>
            <div className="font-mono text-xs bg-gray-100 px-2 py-1 rounded break-all">
              {value || '(not set)'}
            </div>
          </div>
        );
      }

      // Variable mode
      const varName = data.config.variableName;
      const source = data.config.source || 'literal';

      if (!varName) {
        return (
          <div className="text-yellow-600">
            Variable not configured
          </div>
        );
      }

      if (source === 'expression') {
        const expression = data.config.expression || '';
        return (
          <div className="space-y-1">
            <div className="font-semibold">Set Variable</div>
            <div>Variable: <span className="font-mono">{varName}</span></div>
            <div>Source: Expression</div>
            {expression && (
              <div className="font-mono text-xs bg-gray-100 px-2 py-1 rounded break-all">
                {expression}
              </div>
            )}
          </div>
        );
      } else if (source === 'context') {
        const path = data.config.expression || '';
        return (
          <div className="space-y-1">
            <div className="font-semibold">Set Variable</div>
            <div>Variable: <span className="font-mono">{varName}</span></div>
            <div>Source: Context Path</div>
            {path && (
              <div className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                {path}
              </div>
            )}
          </div>
        );
      } else {
        // literal value
        const value = data.config.value;
        let displayValue = '(not set)';

        if (value !== undefined && value !== null && value !== '') {
          if (typeof value === 'string') {
            displayValue = `"${value}"`;
          } else if (typeof value === 'number' || typeof value === 'boolean') {
            displayValue = String(value);
          } else if (value instanceof Date || (typeof value === 'string' && !isNaN(Date.parse(value)))) {
            try {
              displayValue = new Date(value).toLocaleString();
            } catch {
              displayValue = String(value);
            }
          } else {
            displayValue = JSON.stringify(value);
          }
        }

        return (
          <div className="space-y-1">
            <div className="font-semibold">Set Variable</div>
            <div>Variable: <span className="font-mono">{varName}</span></div>
            <div>Source: Literal Value</div>
            <div className="font-mono text-xs bg-gray-100 px-2 py-1 rounded break-all">
              {displayValue}
            </div>
          </div>
        );
      }
    }

    case 'sms': {
      const phoneNumber = data.config.parameters?.phoneNumber;
      const message = data.config.parameters?.messageContent;
      return (
        <div className="space-y-1">
          <div className="font-semibold">SMS Configuration</div>
          <div>Phone: <span className="font-mono">{phoneNumber || '(not set)'}</span></div>
          {message && (
            <div className="max-w-xs">
              <div className="text-xs text-gray-600">Message:</div>
              <div className="text-xs bg-gray-100 px-2 py-1 rounded mt-1 break-words">
                {message.length > 100 ? message.substring(0, 100) + '...' : message}
              </div>
            </div>
          )}
        </div>
      );
    }

    case 'email': {
      const to = data.config.parameters?.to;
      const subject = data.config.parameters?.subject;
      const body = data.config.parameters?.body;
      return (
        <div className="space-y-1">
          <div className="font-semibold">Email Configuration</div>
          <div>To: <span className="font-mono">{to || '(not set)'}</span></div>
          {subject && (
            <div>
              <div className="text-xs text-gray-600">Subject:</div>
              <div className="text-xs">{subject}</div>
            </div>
          )}
          {body && (
            <div className="max-w-xs">
              <div className="text-xs text-gray-600">Body:</div>
              <div className="text-xs bg-gray-100 px-2 py-1 rounded mt-1 break-words">
                {body.length > 100 ? body.substring(0, 100) + '...' : body}
              </div>
            </div>
          )}
        </div>
      );
    }

    case 'webhook': {
      const url = data.config.parameters?.url;
      const method = data.config.parameters?.method || 'POST';
      const body = data.config.parameters?.body;
      return (
        <div className="space-y-1">
          <div className="font-semibold">Webhook Configuration</div>
          <div>Method: <span className="font-mono">{method}</span></div>
          {url && (
            <div className="max-w-xs">
              <div className="text-xs text-gray-600">URL:</div>
              <div className="font-mono text-xs bg-gray-100 px-2 py-1 rounded mt-1 break-all">
                {url}
              </div>
            </div>
          )}
          {body && (
            <div className="max-w-xs">
              <div className="text-xs text-gray-600">Body:</div>
              <div className="font-mono text-xs bg-gray-100 px-2 py-1 rounded mt-1 break-all">
                {body.length > 100 ? body.substring(0, 100) + '...' : body}
              </div>
            </div>
          )}
        </div>
      );
    }

    case 'condition': {
      const field = data.config.field;
      const operator = data.config.operator;
      const value = data.config.value;

      if (!field || !operator) {
        return (
          <div className="text-yellow-600">
            Condition not configured
          </div>
        );
      }

      const operatorDisplay = operator === 'eq' ? '==' :
                             operator === 'ne' ? '!=' :
                             operator === 'gt' ? '>' :
                             operator === 'gte' ? '>=' :
                             operator === 'lt' ? '<' :
                             operator === 'lte' ? '<=' :
                             operator;

      const displayValue = (() => {
        if (value === undefined || value === null || value === '') {
          return '(empty)';
        }
        // Numbers and booleans display without quotes
        if (typeof value === 'number' || typeof value === 'boolean') {
          return String(value);
        }
        // Strings display with quotes
        return JSON.stringify(value);
      })();

      return (
        <div className="space-y-1">
          <div className="font-semibold">Condition</div>
          <div className="font-mono text-xs bg-gray-100 px-2 py-1 rounded break-all">
            {field} {operatorDisplay} {displayValue}
          </div>
          <div className="text-xs text-gray-600 mt-2">
            <span className="text-green-600">● True</span> path if condition matches
          </div>
          <div className="text-xs text-gray-600">
            <span className="text-red-600">● False</span> path if condition fails
          </div>
        </div>
      );
    }

    case 'button_check': {
      const buttonId = data.config.buttonId;

      if (!buttonId) {
        return (
          <div className="text-yellow-600">
            Button not configured
          </div>
        );
      }

      return (
        <div className="space-y-1">
          <div className="font-semibold">Button Check</div>
          <div className="font-mono text-xs bg-gray-100 px-2 py-1 rounded break-all">
            Button: {buttonId}
          </div>
          <div className="text-xs text-gray-600 mt-2">
            <span className="text-green-600">● Pressed</span> path if button was clicked
          </div>
          <div className="text-xs text-gray-600">
            <span className="text-gray-500">● Not Pressed</span> path to continue loop
          </div>
        </div>
      );
    }

    default:
      return null;
  }
};

// Custom node component
export const CustomNode: React.FC<NodeProps<WorkflowNodeData>> = ({ data, selected }) => {
  const config = nodeTypeConfig[data.type as keyof typeof nodeTypeConfig] || nodeTypeConfig.action;
  const Icon = config.icon;
  const isEndNode = data.type === 'end' || data.type === 'goto_start'; // goto_start also has no output handles
  const tooltipContent = getNodeTooltipContent(data);

  // Determine background color based on debug state or default (using inline styles for dynamic updates)
  const getBackgroundStyle = (): React.CSSProperties => {
    // Debug mode colors take precedence
    if (data.debugStatus === 'error') {
      return { backgroundColor: '#FEE2E2' }; // red-100
    }
    if (data.debugStatus === 'completed') {
      return { backgroundColor: '#DCFCE7' }; // green-100
    }
    // Default: disabled trigger check
    if (data.type === 'start' &&
        (data.config?.triggerType === 'scheduled' || data.config?.triggerType === 'mqtt') &&
        data.config?.enabled === false) {
      return { backgroundColor: '#FECACA' }; // red-200
    }
    return { backgroundColor: '#FFFFFF' }; // white
  };

  // Determine border color based on debug state or selection (using inline styles for dynamic updates)
  const getBorderStyle = (): React.CSSProperties => {
    if (data.debugStatus === 'error') {
      return { borderColor: '#F87171' }; // red-400
    }
    if (data.debugStatus === 'completed') {
      return { borderColor: '#4ADE80' }; // green-400
    }
    return { borderColor: selected ? '#3B82F6' : '#D1D5DB' }; // blue-500 or gray-300
  };

  const nodeContent = (
    <div
      className={cn(
        'px-4 py-3 rounded-lg border-2 shadow-md min-w-[160px] relative',
        selected ? 'ring-2 ring-blue-200' : '',
        'hover:shadow-lg transition-shadow'
      )}
      style={{ ...getBackgroundStyle(), ...getBorderStyle() }}
    >
      {/* Execution count badge - top right corner */}
      {data.executionCount > 0 && (
        <div className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shadow">
          {data.executionCount > 99 ? '99+' : data.executionCount}
        </div>
      )}
      {/* Input handle (target) - Left side only for horizontal flow */}
      {data.type !== 'start' && (
        <Handle
          type="target"
          position={Position.Left}
          id="target-left"
          className="!bg-blue-500 !w-3 !h-3"
          isConnectable={true}
        />
      )}

      <div className="flex items-center gap-2 mb-2">
        <div className={cn('w-8 h-8 rounded flex items-center justify-center', config.color)}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm text-gray-900 truncate">
            {data.label || config.label}
          </div>
          {data.type === 'start' && data.config?.triggerType === 'scheduled' && (
            <div className="text-xs text-purple-600 font-medium mt-0.5">
              Time Trigger
            </div>
          )}
          {data.type === 'start' && data.config?.triggerType === 'mqtt' && data.config?.mqttTopic && (
            <div className="text-xs text-green-600 font-medium mt-0.5">
              MQTT: {data.config.mqttTopic}
            </div>
          )}
        </div>
      </div>
      {data.description && (
        <div className="text-xs text-gray-600 line-clamp-2">{data.description}</div>
      )}

      {/* Loop pairing indicator for End nodes */}
      {data.type === 'end' && data.config?.pairedStartName && (
        <div className="text-xs text-blue-600 font-medium mt-1 flex items-center gap-1">
          <span>↻</span>
          <span>Loops to: {data.config.pairedStartName}</span>
        </div>
      )}

      {/* Loop name indicator for Start nodes */}
      {data.type === 'start' && data.config?.name && data.config?.pairedEndId && (
        <div className="text-xs text-blue-600 font-medium mt-1 flex items-center gap-1">
          <span>↻</span>
          <span>{data.config.name}</span>
        </div>
      )}

      {/* Target indicator for Goto Start nodes */}
      {data.type === 'goto_start' && data.config?.targetStartName && (
        <div className="text-xs text-purple-600 font-medium mt-1 flex items-center gap-1">
          <span>→</span>
          <span>To: {data.config.targetStartName}</span>
        </div>
      )}

      {/* Output handle (source) - Right side only for horizontal flow */}
      {!isEndNode && !['condition', 'switch', 'button_check', 'http_request', 'send_data'].includes(data.type) && (
        <Handle
          type="source"
          position={Position.Right}
          id="source-right"
          className="!bg-green-500 !w-3 !h-3"
          isConnectable={true}
        />
      )}

      {/* Special labeled handles for branching nodes - Right side with vertical offset */}
      {(data.type === 'condition' || data.type === 'switch') && !isEndNode && (
        <>
          <Handle
            type="source"
            position={Position.Right}
            id="true"
            className="!bg-green-500 !w-4 !h-4"
            style={{ top: '35%' }}
            isConnectable={true}
          />
          <Handle
            type="source"
            position={Position.Right}
            id="false"
            className="!bg-red-500 !w-4 !h-4"
            style={{ top: '65%' }}
            isConnectable={true}
          />
        </>
      )}

      {/* Special labeled handles for button_check nodes - Right side with vertical offset */}
      {data.type === 'button_check' && !isEndNode && (
        <>
          <Handle
            type="source"
            position={Position.Right}
            id="pressed"
            className="!bg-green-500 !w-4 !h-4"
            style={{ top: '35%' }}
            isConnectable={true}
          />
          <Handle
            type="source"
            position={Position.Right}
            id="not_pressed"
            className="!bg-gray-400 !w-4 !h-4"
            style={{ top: '65%' }}
            isConnectable={true}
          />
        </>
      )}

      {/* Special labeled handles for http_request nodes - Right side with vertical offset */}
      {data.type === 'http_request' && !isEndNode && (
        <>
          <Tooltip>
            <TooltipTrigger asChild>
              <Handle
                type="source"
                position={Position.Right}
                id="success"
                className="!bg-green-500 !w-4 !h-4"
                style={{ top: '25%' }}
                isConnectable={true}
              />
            </TooltipTrigger>
            <TooltipPortal>
              <TooltipContent side="right" className="max-w-xs">
                <div className="font-semibold text-green-600">Success (2xx)</div>
                <div className="text-xs text-gray-600">HTTP 200-299 responses</div>
              </TooltipContent>
            </TooltipPortal>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Handle
                type="source"
                position={Position.Right}
                id="bypass"
                className="!bg-yellow-500 !w-4 !h-4"
                style={{ top: '50%' }}
                isConnectable={true}
              />
            </TooltipTrigger>
            <TooltipPortal>
              <TooltipContent side="right" className="max-w-xs">
                <div className="font-semibold text-yellow-600">Bypass (3xx/1xx)</div>
                <div className="text-xs text-gray-600">Redirects (300-399) or Info (100-199)</div>
              </TooltipContent>
            </TooltipPortal>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Handle
                type="source"
                position={Position.Right}
                id="failure"
                className="!bg-red-500 !w-4 !h-4"
                style={{ top: '75%' }}
                isConnectable={true}
              />
            </TooltipTrigger>
            <TooltipPortal>
              <TooltipContent side="right" className="max-w-xs">
                <div className="font-semibold text-red-600">Failure (4xx/5xx)</div>
                <div className="text-xs text-gray-600">Client errors (400-499) or Server errors (500-599)</div>
              </TooltipContent>
            </TooltipPortal>
          </Tooltip>
        </>
      )}

      {/* Special labeled handles for send_data nodes - Right side with vertical offset */}
      {data.type === 'send_data' && !isEndNode && (
        <>
          <Tooltip>
            <TooltipTrigger asChild>
              <Handle
                type="source"
                position={Position.Right}
                id="success"
                className="!bg-green-500 !w-4 !h-4"
                style={{ top: '20%' }}
                isConnectable={true}
              />
            </TooltipTrigger>
            <TooltipPortal>
              <TooltipContent side="right" className="max-w-xs">
                <div className="font-semibold text-green-600">Success</div>
                <div className="text-xs text-gray-600">Data sent successfully</div>
              </TooltipContent>
            </TooltipPortal>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Handle
                type="source"
                position={Position.Right}
                id="error"
                className="!bg-red-500 !w-4 !h-4"
                style={{ top: '40%' }}
                isConnectable={true}
              />
            </TooltipTrigger>
            <TooltipPortal>
              <TooltipContent side="right" className="max-w-xs">
                <div className="font-semibold text-red-600">Error</div>
                <div className="text-xs text-gray-600">Send failed (offline, connection lost)</div>
              </TooltipContent>
            </TooltipPortal>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Handle
                type="source"
                position={Position.Right}
                id="buffered"
                className="!bg-blue-500 !w-4 !h-4"
                style={{ top: '60%' }}
                isConnectable={true}
              />
            </TooltipTrigger>
            <TooltipPortal>
              <TooltipContent side="right" className="max-w-xs">
                <div className="font-semibold text-blue-600">Buffered</div>
                <div className="text-xs text-gray-600">Data buffered for later transmission</div>
              </TooltipContent>
            </TooltipPortal>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Handle
                type="source"
                position={Position.Right}
                id="bypass"
                className="!bg-yellow-500 !w-4 !h-4"
                style={{ top: '80%' }}
                isConnectable={true}
              />
            </TooltipTrigger>
            <TooltipPortal>
              <TooltipContent side="right" className="max-w-xs">
                <div className="font-semibold text-yellow-600">Bypass</div>
                <div className="text-xs text-gray-600">Skipped due to rate limiting</div>
              </TooltipContent>
            </TooltipPortal>
          </Tooltip>
        </>
      )}
    </div>
  );

  // Wrap with tooltip if there's content to show
  if (tooltipContent) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          {nodeContent}
        </TooltipTrigger>
        <TooltipPortal>
          <TooltipContent side="right" className="max-w-sm bg-yellow-50 border border-gray-300">
            {tooltipContent}
          </TooltipContent>
        </TooltipPortal>
      </Tooltip>
    );
  }

  return nodeContent;
};

// Custom edge component with delete button and true/false labels
export const CustomEdge: React.FC<EdgeProps> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  selected,
  sourceHandleId: sourceHandle,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [edgePath, labelX, labelY] = React.useMemo(() => {
    // Use React Flow's built-in smooth step path for intelligent routing
    const [path, centerX, centerY] = getSmoothStepPath({
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY,
      targetPosition,
      borderRadius: 8,
    });
    return [path, centerX, centerY];
  }, [sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition]);

  const onEdgeDelete = useCallback((evt: React.MouseEvent) => {
    evt.stopPropagation();
    // Trigger edge deletion
    const event = new CustomEvent('deleteEdge', { detail: { id } });
    window.dispatchEvent(event);
  }, [id]);

  // Determine if this is a branching path (condition true/false, button pressed/not_pressed, http success/failure/bypass, or send_data success/error/buffered/bypass)
  const isConditionEdge = sourceHandle === 'true' || sourceHandle === 'false';
  const isButtonCheckEdge = sourceHandle === 'pressed' || sourceHandle === 'not_pressed';
  const isHttpRequestEdge = sourceHandle === 'success' || sourceHandle === 'failure' || sourceHandle === 'bypass';
  const isSendDataEdge = sourceHandle === 'success' || sourceHandle === 'error' || sourceHandle === 'buffered' || sourceHandle === 'bypass';
  const isBranchingEdge = isConditionEdge || isButtonCheckEdge || isHttpRequestEdge || isSendDataEdge;
  const isPositivePath = sourceHandle === 'true' || sourceHandle === 'pressed' || sourceHandle === 'success';
  const isErrorPath = sourceHandle === 'failure' || sourceHandle === 'error';
  const isBypassPath = sourceHandle === 'bypass';
  const isBufferedPath = sourceHandle === 'buffered';
  const edgeColor = isBranchingEdge
    ? (isPositivePath ? '#10b981' : (isErrorPath ? '#ef4444' : (isBypassPath ? '#eab308' : (isBufferedPath ? '#3b82f6' : '#9ca3af'))))  // green for positive, red for error/failure, yellow for bypass, blue for buffered, gray for other
    : (selected ? '#3b82f6' : '#94a3b8');

  return (
    <>
      <path
        id={id}
        style={{
          ...style,
          strokeWidth: selected ? 3 : 2,
          stroke: edgeColor,
        }}
        className={cn('react-flow__edge-path', selected && 'stroke-blue-500')}
        d={edgePath}
        markerEnd={markerEnd}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      />
      {/* Branching Edge Label (True/False or Pressed/Not Pressed) */}
      {isBranchingEdge && (
        <foreignObject
          width={80}
          height={20}
          x={labelX - 40}
          y={labelY - 35}
          className="pointer-events-none"
          requiredExtensions="http://www.w3.org/1999/xhtml"
        >
          <div className="flex items-center justify-center w-full h-full">
            <div
              className={cn(
                'px-2 py-0.5 rounded text-xs font-semibold shadow-sm',
                isPositivePath
                  ? 'bg-green-500 text-white'
                  : isErrorPath
                  ? 'bg-red-500 text-white'
                  : isBypassPath
                  ? 'bg-yellow-500 text-black'
                  : isBufferedPath
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-500 text-white'
              )}
            >
              {isConditionEdge
                ? (isPositivePath ? 'True' : 'False')
                : isHttpRequestEdge || isSendDataEdge
                ? (sourceHandle === 'success' ? 'Success' : isErrorPath ? 'Error' : isBypassPath ? 'Bypass' : isBufferedPath ? 'Buffered' : sourceHandle)
                : (isPositivePath ? 'Pressed' : 'Not Pressed')}
            </div>
          </div>
        </foreignObject>
      )}
      {/* Delete Button */}
      {(selected || isHovered) && (
        <foreignObject
          width={24}
          height={24}
          x={labelX - 12}
          y={labelY - 12}
          className="edgebutton-foreignobject"
          requiredExtensions="http://www.w3.org/1999/xhtml"
        >
          <div className="flex items-center justify-center w-full h-full">
            <button
              className="w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center cursor-pointer shadow-lg transition-colors"
              onClick={onEdgeDelete}
              title="Delete connection"
            >
              ×
            </button>
          </div>
        </foreignObject>
      )}
    </>
  );
};

export const nodeTypes: NodeTypes = {
  custom: CustomNode,
  rectangle_annotation: RectangleAnnotation,
  label_annotation: LabelAnnotation,
};

export const edgeTypes = {
  default: CustomEdge,
  smoothstep: CustomEdge,
};
